import { AxiosInstance } from 'axios';
import { usePageStore } from '@/store/page.store';

let requests_tracker_count = 0;
let timer;

const unauthorizedStates = [401, 403];

export function createInterceptors(axiosInstance: AxiosInstance) {
    axiosInstance.interceptors.request.use(
        (config: any) => {
            handleFileDownloadRequest(config);
            updateLoadingState({ count: 1 });
            return config;
        },
        (error: any) => {
            updateLoadingState({ count: -1 });
            return Promise.reject(error);
        }
    );

    axiosInstance.interceptors.response.use(
        (response: any) => {
            updateLoadingState({ count: -1 });
            return response;
        },
        async (error: any) => {
            updateLoadingState({ count: -1 });
            if (unauthorizedStates.includes(error.response?.status)) {
                await redirectToUnauthorized();
            }
            await handleFileDownloadResponseError(error);
            return Promise.reject(error);
        }
    );

    /*
     * Modify headers for file download requests
     * Sets the Accept header to handle both binary and JSON responses
     */
    const handleFileDownloadRequest = (config: any) => {
        if (config.responseType && config.responseType === 'blob') {
            config.headers['Accept'] = 'application/octet-stream, application/json';
        }
    };

    /*
     * Handle errors for file download requests
     * Reads the Blob response as text if it's an error, so that it can be parsed as JSON later via the generated clients
     */
    const handleFileDownloadResponseError = async (error: any) => {
        const { response } = error;
        if (
            response &&
            response.status !== 200 &&
            response.data instanceof Blob &&
            response.data.type === 'application/json'
        ) {
            const text = await response.data.text();
            response.data = text;
        }
    };

    const updateLoadingState = ({ count }: { count: number }) => {
        const waitTimeInMs = 500;
        if (count >= 0) {
            requests_tracker_count = requests_tracker_count + count;
        } else {
            requests_tracker_count = Math.max(0, requests_tracker_count - Math.abs(count));
        }

        if (timer) {
            clearTimeout(timer);
        }

        if (requests_tracker_count == 0) {
            usePageStore().setIsLoading(false);
        }

        timer = setTimeout(() => {
            usePageStore().setIsLoading(requests_tracker_count > 0);
        }, waitTimeInMs);
    };

    /*
     * Show the unauthorized page when the api reports that the user has insufficient rights.
     * The user stays logged in, only the requested data is off limits.
     *
     * The import has to stay lazy. A top level `import router from '@/router'` closes the cycle
     * @/router -> @/store -> @/api -> this module -> @/router, and both ends of that cycle read
     * their import at module level: @/router builds its routes from `stores`, and the stores call
     * createInstance() at module level. Whichever end is evaluated second throws a ReferenceError
     * before the app ever mounts.
     */
    const redirectToUnauthorized = async () => {
        const router = (await import('@/router')).default;
        if (router.currentRoute.value.name !== 'unauthorized') {
            await router.push({ name: 'unauthorized' });
        }
    };
}
